import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { HomeSeasonMotivation } from "@/types/homeMotivation"

type Props = {
  season: HomeSeasonMotivation
}

function formatCoins(n: number): string {
  return n.toLocaleString("ru-RU")
}

export default function SeasonChampionBanner({ season }: Props) {
  const navigate = useNavigate()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const city = season.cityName?.trim() || "город"
  const prize = season.top1?.prizeCoins ?? season.prizes.top1
  const topName = season.top1?.nickname ?? "Место свободно"
  const fundLabel = season.canPayout
    ? `~${formatCoins(prize)}`
    : `казна ${formatCoins(season.championFundCoins)}`

  const pastHref =
    season.cityId != null
      ? `/play/past-champions?cityId=${season.cityId}`
      : "/play/past-champions"

  return (
    <>
      <button
        type="button"
        className="w-full rounded-2xl border border-guardians-accent/35 bg-guardians-accent/[0.08] p-3.5 text-left active:opacity-90"
        onClick={() => setDetailsOpen(true)}
      >
        <div className="flex flex-row items-center gap-2">
          <span className="text-lg">🏆</span>
          <span className="truncate text-[11px] font-bold uppercase tracking-wide text-guardians-accent">
            Чемпион {city} · {season.seasonTitle || "сезон"}
          </span>
        </div>
        <div className="mt-2 flex flex-row items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-base font-extrabold text-white">
              {topName}
            </div>
            <div className="text-xs font-semibold text-white/55">
              {season.top1
                ? `${formatCoins(season.top1.localPts)} очк. города`
                : "Набери очки первым"}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-sm font-extrabold text-[#FB923C]">
              {fundLabel}
            </div>
            <div className="text-[10px] font-semibold text-[#FB923C]/75">
              {season.canPayout ? "топ-1 заберёт" : "до мин. казны"}
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-row items-center justify-between gap-2 text-xs font-bold">
          {season.me.isTop1 ? (
            <span className="text-green-400">Ты №1 · удерживай место</span>
          ) : (
            <span className="min-w-0 truncate text-white/70">
              Ты #{season.me.rank ?? "—"} · {formatCoins(season.me.localPts)}{" "}
              лок.
              {season.me.ptsToTop1 > 0
                ? ` · до 1: ${formatCoins(season.me.ptsToTop1)}`
                : ""}
            </span>
          )}
          <span className="shrink-0 text-white/45">
            {season.daysLeft}д до выплаты
          </span>
        </div>
      </button>

      {detailsOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-6"
          role="dialog"
          onClick={() => setDetailsOpen(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-guardians-accent/30 bg-[#12121f] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-black text-guardians-accent">
              Фонд чемпионов
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-white/65">
              Казна: {formatCoins(season.championFundCoins)} монет. 1-го числа
              топ-3 по очкам города (локальные) получают 50% / 30% / 20%, затем
              очки города сбрасываются.
            </p>

            {season.top3.length > 0 ? (
              <ul className="mt-3 space-y-1 border-t border-white/10 pt-2">
                {season.top3.map((row, index) => (
                  <li
                    key={row.userId}
                    className="flex items-center gap-2 border-b border-white/[0.06] py-2 text-sm"
                  >
                    <span className="w-7 font-extrabold text-guardians-accent">
                      #{index + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-bold text-white">
                      {row.nickname}
                    </span>
                    <span className="text-right font-extrabold text-white">
                      {formatCoins(row.localPts)}
                    </span>
                    <span className="w-14 text-right font-extrabold text-[#FB923C]">
                      {formatCoins(row.prizeCoins)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            <button
              type="button"
              className="mt-4 w-full rounded-xl border border-guardians-accent/40 bg-guardians-accent/15 py-3 text-sm font-extrabold text-guardians-accent"
              onClick={() => {
                setDetailsOpen(false)
                navigate("/play/leaderboard")
              }}
            >
              Рейтинг города
            </button>
            <Link
              to={pastHref}
              className="mt-2 block w-full rounded-xl border border-guardians-accent/40 bg-guardians-accent/15 py-3 text-center text-sm font-extrabold text-guardians-accent"
              onClick={() => setDetailsOpen(false)}
            >
              Прошлые победители
            </Link>
            <button
              type="button"
              className="mt-3 w-full py-2 text-sm font-semibold text-white/45"
              onClick={() => setDetailsOpen(false)}
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}
    </>
  )
}
